import { useEffect, useMemo, useState } from 'react';
import { useIcibAdmin } from '../context/IcibAdminContext';
import { useDrawer } from '../../drawer/DrawerContext';
import { useToast } from '../../toast/ToastContext';
import { parseCsv } from '../utils/csv';
import Pagination, { usePagination } from '../components/Pagination';
import DefinitionList from '../components/DefinitionList';
import LifeRail from '../components/LifeRail';
import ProductMultiSelect from '../components/ProductMultiSelect';
import { fmtDate, fmtDT, phoneChars, resultRowEmail, todayISO } from '../utils/format';
import { withProductColumns, productDropdowns, prepareUploadRows, columnHeadings, withHeadings, fromHeadings, COLUMN_HEADINGS } from '../utils/productColumns';
// utils/xlsx.js pulls in exceljs (a genuinely large library) purely to
// build/read the one .xlsx template with a product dropdown — dynamically
// imported below so it's its own chunk, only fetched when a user actually
// downloads or uploads an .xlsx, instead of bloating everyone's initial
// bundle for a feature most page loads never touch.
const loadXlsxUtils = () => import('../utils/xlsx');

// §2.6a — today's date as a YYYY-MM-DD default for effective_date fields:
// today or earlier applies a row immediately (the behavior this view
// always had); anything later defers it to a PendingIntakeRow instead
// (§ICIB-13). Defaulting to today rather than blank means "apply now" is
// the zero-effort path, matching pre-§2.6a behavior for anyone who
// doesn't need to backdate/schedule. Taken in NZ time — the same calendar
// the backend's `intake.nz_today()` uses to decide pending vs. immediate —
// not toISOString()'s UTC date, which lags NZ by a day every morning.
function today() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Pacific/Auckland' }).format(new Date());
}

// Per-action column sets, from the identifying-fields table in
// BrandonUserStoryAPI.md §ICIB-02 / README §2.6/§2.8: `email` alone
// identifies the target on every action, but ADDITION/REMOVAL also carry
// `subsidised_product_level` (ICIB-174) — a JSON array of one or more
// product ids on the bulk endpoint, since a row can now select more than
// one product at once instead of always touching every active product.
// Update is personal-details-only — no product, and no form_data fields
// (a non-profile field is rejected, not dropped). `action` is pre-filled
// as a starter row so the only thing left to fill in per person is the
// actual data. In the .xlsx the subsidised_product_level slot becomes one
// Yes/No column per product, and company_code isn't a column at all — it's
// not sent at upload (utils/productColumns.js). `effective_date` is required on every
// action (§2.6a); `reason` only on ADDITION/REMOVAL — UPDATE rejects it
// outright, same guard as a stray form_data field (ICIB-02b error2).
// `gender` (a sixth profile field, §2.1/§2.8) is on every action that
// touches profile fields.
// Column order follows ICIB's own Additions/Removals spreadsheet (EXTR-13):
// who the person is (surname, first name, dob, sex, then any form_data
// fields such as nz_citizen_or_visa — see withFormDataColumns), how to
// reach them (email, address, phone), then the movement itself (effective
// date, action, reason, product columns). Upload reads cells by header,
// not position, so the order is purely for the person filling it in.
const ACTION_COLUMNS = {
  ADDITION: ['last_name', 'first_name', 'dob', 'gender', 'email', 'address', 'phone', 'effective_date', 'action', 'reason', 'subsidised_product_level'],
  UPDATE: ['last_name', 'first_name', 'dob', 'gender', 'email', 'address', 'phone', 'effective_date', 'action'],
  REMOVAL: ['email', 'effective_date', 'action', 'reason', 'subsidised_product_level'],
};
// Slots the group's form_data field_keys in right after `gender`, where
// the sample sheet keeps its NZ citizen/visa column, rather than tacking
// them on after the product columns.
function withFormDataColumns(columns, fieldKeys) {
  return columns.flatMap((c) => (c === 'gender' ? [c, ...fieldKeys] : [c]));
}
// Which of each action's fixed columns are calendar dates — used by the
// .xlsx download to format those columns as YYYY-MM-DD and note that on
// the header cell, since Excel otherwise free-formats a date column and a
// pasted/typed value can silently land in a format the API rejects.
const DATE_COLUMNS = {
  ADDITION: ['dob', 'effective_date'],
  UPDATE: ['dob', 'effective_date'],
  REMOVAL: ['effective_date'],
};
// Per-action overrides of the shared COLUMN_HEADINGS (EXTR-13). An Update
// row only corrects personal details — no employment starts or ends — so
// "Employment or Termination Effective Date" reads wrong on its template.
const ACTION_HEADINGS = {
  UPDATE: { effective_date: 'Update Effective Date' },
};
// `effective_date`/`reason` (§2.6a) are reserved, guaranteed columns too —
// same as action/email — carried on the template-field API as their own
// is_effective_date_field/is_reason_field flags. Without excluding them
// here, a group whose active template has these as real TemplateField
// rows would have them show up a second time via customFields.map,
// duplicating the fixed Effective date/Reason inputs already rendered
// above. `is_product_field` (the guaranteed subsidised_product_level
// field, §2.7/§2.8) and `maps_to_user_field` (now including `gender`,
// §2.1) are excluded the same way — both are rendered as their own fixed
// inputs below, not via customFields.map.
const NOT_A_FORM_DATA_FIELD = (f) => f.is_action_field || f.is_email_field || f.is_product_field || f.maps_to_user_field || f.is_effective_date_field || f.is_reason_field;

/* ICIB-02 — Process employee additions, leavers and changes.
   Split into three independent flows, matching the three distinct
   branches of the intake pipeline (README §2.6): Addition, Update,
   Removal. Each tab offers both single-item and bulk-CSV forms of that
   same action:
     Addition -> POST   /companies/{company_id}/employees                (single, ICIB-02a)
     Update   -> PATCH  /companies/{company_id}/employees/{id}            (single, ICIB-02b)
     Removal  -> POST   /companies/{company_id}/employees/bulk-upload (single REMOVAL row, ICIB-02c —
                 identifies the target by email + subsidised_product_level (ICIB-174) and ends
                 only the enrolment(s) for the product(s) selected, same as a bulk file; there's
                 no single-item DELETE call here since that endpoint still needs one specific
                 enrolment_id, §2.8)
   Bulk is a *single* real endpoint underneath all three tabs — POST
   /companies/{company_id}/employees/bulk-upload — since one uploaded file can
   freely mix ADDITION/UPDATE/REMOVAL rows via each row's own `action`
   column (README §2.6). Surfacing it once per tab (rather than once for
   the whole view) keeps the "separate" framing consistent with the
   single-item forms above it, while BulkUploadPanel below still accepts
   — and doesn't fight — a file that turns out to be mixed. */
function IntakeView() {
  const [tab, setTab] = useState('addition');

  return (
    <>
      <div className="eyebrow">Additions, changes and leavers</div>
      <div className="page-h"><h2>Enrolment</h2></div>
      <p className="page-sub">
        Three separate movements, three separate actions — an addition invites someone onto one or
        more products, an update corrects personal details only, and a removal ends the enrolment(s)
        for whichever product(s) are selected. Each tab handles one person at a time or a whole CSV
        file at once.
      </p>

      <div className="tabs" role="tablist">
        <button className="tab" role="tab" aria-selected={tab === 'addition'} onClick={() => setTab('addition')}>Addition</button>
        <button className="tab" role="tab" aria-selected={tab === 'update'} onClick={() => setTab('update')}>Update</button>
        <button className="tab" role="tab" aria-selected={tab === 'removal'} onClick={() => setTab('removal')}>Removal</button>
      </div>

      {tab === 'addition' && <AdditionPanel />}
      {tab === 'update' && <UpdatePanel />}
      {tab === 'removal' && <RemovalPanel />}
    </>
  );
}

const EMPTY_ADDITION = { email: '', first_name: '', last_name: '', dob: '', phone: '', address: '', gender: '', subsidised_product_level: [], effective_date: '', reason: '' };
// Defaults effective_date to today so "apply immediately" (the pre-§2.6a
// behavior) stays the zero-effort path — a function rather than a shared
// constant so the form resets to *today's* date after each submit, not
// whatever today was when the module first loaded.
function emptyAddition() {
  return { ...EMPTY_ADDITION, effective_date: today() };
}

// ICIB-174 — the product picker is multi-select. The single-item
// POST /companies/{company_id}/employees only ever takes one product id, so one
// product still goes there; two or more go through the bulk-upload
// endpoint as a single ADDITION row carrying the id array (all-or-nothing
// across the row's products, one enrolment each), same one-row-bulk trick
// RemovalPanel/LeaversView use. Normalises both responses to
// `{ error, message }` or `{ enrolments: [...], scheduled: [...] }` —
// §2.6a: each product is only enrolled once effective_date + its
// eligibility_days has arrived, so one submit can enrol some products now
// and hold the rest (`partially_scheduled`), each held product with its
// own `apply_on`. The single-item 202 names no product, so its one held
// entry is the one product that was sent.
async function submitAddition(fields, selected, addOne, bulkUploadEmployees) {
  if (selected.length === 1) {
    const res = await addOne({ ...fields, subsidised_product_level: selected[0] });
    if (res?.error) return res;
    if (res?.status === 'scheduled') {
      return { enrolments: [], scheduled: [{ group_product_id: selected[0], effective_date: res.effective_date, apply_on: res.apply_on }] };
    }
    return { enrolments: [res], scheduled: [] };
  }
  const res = await bulkUploadEmployees('single-addition.csv', [{ ...fields, action: 'ADDITION', subsidised_product_level: selected }]);
  if (res?.error) return res;
  const row = res?.results?.[0];
  if (!res?.committed || !row || row.status === 'error') return { error: row?.status || 'rejected', message: row?.message };
  return { enrolments: row.enrolments || [], scheduled: row.scheduled || [] };
}

// Toast text for submitAddition's result — "Invited — 1 enrolment
// created. Scheduled — Life on 2026-10-30 …" for a partially_scheduled
// row; either half alone otherwise.
function additionMessage({ enrolments, scheduled }, productName) {
  const parts = [];
  if (enrolments.length) parts.push(`Invited — ${enrolments.length} enrolment${enrolments.length === 1 ? '' : 's'} created.`);
  if (scheduled.length) {
    const when = scheduled.map((s) => `${productName(s.group_product_id)} on ${s.apply_on || s.effective_date}`).join(', ');
    parts.push(`Scheduled — ${when}, once the eligibility period has passed (see Pending enrolment).`);
  }
  return parts.join(' ');
}

// §2.6a — extra detail on a committed bulk row's result line: how many of
// a partially_scheduled row's products were enrolled now, when a held row
// applies (earliest apply_on across an Addition's held products;
// effective_date for an Update/Removal), and any pending Additions a
// Removal cancelled instead of ending an enrolment (cancelled_pending).
function rowDetail(r) {
  const parts = [];
  if (r.status === 'partially_scheduled') parts.push(`${(r.enrolments || []).length} enrolled now`);
  if (r.status === 'scheduled' || r.status === 'partially_scheduled') {
    const held = r.scheduled || [];
    const next = held.map((s) => s.apply_on || s.effective_date).filter(Boolean).sort()[0] || r.apply_on || r.effective_date;
    if (held.length) parts.push(`${held.length} scheduled, next applies ${next}`);
    else if (next) parts.push(`scheduled for ${next}`);
  }
  const cancelled = (r.cancelled_pending_additions || []).length;
  if (cancelled) parts.push(`cancelled ${cancelled} pending addition${cancelled === 1 ? '' : 's'}`);
  return parts.length ? ` — ${parts.join(', ')}` : '';
}

function AdditionPanel() {
  const { groupId, curGroup, gProducts, activeTemplates, loadActiveTemplate } = useIcibAdmin();
  const { addOne, bulkUploadEmployees } = useIcibAdmin();
  const toast = useToast();
  const [form, setForm] = useState(emptyAddition);
  const [custom, setCustom] = useState({});
  const [busy, setBusy] = useState(false);

  // §2.8/ICIB-174 — multi-select; see submitAddition above for how one
  // vs several products are sent.
  // gProducts(groupId) includes removed/retired pairings too (ICIB-11f
  // fetched with ?include_inactive=true, for ProductsView's reactivate
  // flow) — filter those out so only currently-active products are
  // selectable.
  const products = groupId ? gProducts(groupId).filter((gp) => gp.is_active !== false) : [];
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setCustomField = (k) => (e) => setCustom((c) => ({ ...c, [k]: e.target.value }));

  // Addition also writes group-specific form_data fields (README §2.6) —
  // pull the group's active template's custom fields (full label/type/
  // required, not just the ICIB-11l key list BulkUploadPanel uses below)
  // so this single-item form can collect them too, instead of only
  // offering them via a file upload. ICIB-10a's `?active=true` filter
  // returns the active template *with fields* in one call, so this no
  // longer needs the old loadTemplates (list) -> activeTemplate() ->
  // loadTemplateDetail(tpl.id) three-step round trip.
  useEffect(() => { if (groupId) loadActiveTemplate(groupId); }, [groupId, loadActiveTemplate]);
  // undefined = not fetched yet for this group, null = fetched, group has
  // no active template (§ICIB-10a error2) — only the loaded-object case
  // has fields to show.
  const tplEntry = groupId ? activeTemplates[groupId] : undefined;
  const tplFields = tplEntry ? tplEntry.fields : undefined;
  const customFields = tplFields ? tplFields.filter((f) => !NOT_A_FORM_DATA_FIELD(f)) : [];

  async function submit(e) {
    e.preventDefault();
    if (!groupId) return;
    const selected = form.subsidised_product_level;
    if (!selected.length) { toast('Select at least one product.', 'bad'); return; }
    setBusy(true);
    const res = await submitAddition({ ...form, ...custom }, selected, addOne, bulkUploadEmployees);
    setBusy(false);
    if (res?.error) {
      toast(res.message || 'Could not add this enrolment.', 'bad', res.error);
      return;
    }
    // §2.6a — a product not yet eligible (effective_date + eligibility_days
    // in the future) is held as a PendingIntakeRow instead of enrolled now;
    // the toast names each held product with the date it will apply.
    toast(additionMessage(res, (id) => products.find((p) => p.id === id)?.name || id), 'ok');
    setForm(emptyAddition());
    setCustom({});
  }

  if (!groupId) return <ScopeNeeded />;

  return (
    <>
      <div className="card card-b">
        <div className="card-h" style={{ padding: 0, border: 0, marginBottom: 10 }}>
          <h4>New addition — {curGroup()?.name}</h4>
          <span className="spacer" />
          <span className="ep">POST /companies/{'{company_id}'}/employees</span>
        </div>
        {tplEntry === null && (
          <div className="note bad" style={{ marginBottom: 14 }}>
            <b>Attention</b>You have not created any template — please create one under Enrolment forms before adding anyone.
          </div>
        )}
        <form onSubmit={submit}>
          <div className="grid g2">
            <div className="field">
              <label>Email <span className="req">*</span></label>
              <input type="email" required value={form.email} onChange={set('email')} placeholder="hemi.walker@example.co.nz" />
              <div className="hint">Looked up to resolve or create the person's account — not editable after intake.</div>
            </div>
            <div className="field">
              <label>First name <span className="req">*</span></label>
              <input required value={form.first_name} onChange={set('first_name')} />
            </div>
            <div className="field">
              <label>Last name <span className="req">*</span></label>
              <input required value={form.last_name} onChange={set('last_name')} />
            </div>
            <div className="field">
              <label>Date of birth <span className="req">*</span></label>
              <input type="date" required max={todayISO()} value={form.dob} onChange={set('dob')} />
              <div className="hint">Always required, even if this person already has an account.</div>
            </div>
            <div className="field">
              <label>Phone</label>
              <input type="tel" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: phoneChars(e.target.value) }))} />
            </div>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>Address</label>
              <input value={form.address} onChange={set('address')} />
            </div>
            <div className="field">
              <label>Gender <span className="req">*</span></label>
              <select required value={form.gender} onChange={set('gender')}>
                <option value="" disabled>Select…</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div className="field">
              <label>Subsidised product level <span className="req">*</span></label>
              <ProductMultiSelect
                products={products}
                value={form.subsidised_product_level}
                onChange={(ids) => setForm((f) => ({ ...f, subsidised_product_level: ids }))}
                disabled={!products.length}
              />
              <div className="hint">Pick one or more — one enrolment is created per product.</div>
            </div>
            <div className="field">
              <label>Employment Effective Date <span className="req">*</span></label>
              <input type="date" required value={form.effective_date} onChange={set('effective_date')} />
              <div className="hint">Each product is enrolled (and its invitation sent) once this date plus that product's eligibility period has passed — until then it's scheduled, see Pending enrolment.</div>
            </div>
            <div className="field">
              <label>Reason <span className="req">*</span></label>
              <input required value={form.reason} onChange={set('reason')} placeholder="e.g. New starter" />
            </div>
            {customFields.map((f) => (
              <div className="field" key={f.id}>
                <label>{f.label}{f.required && <span className="req">*</span>}</label>
                {f.field_type === 'dropdown' ? (
                  <select required={!!f.required} value={custom[f.field_key] || ''} onChange={setCustomField(f.field_key)}>
                    <option value="" disabled>Select…</option>
                    {(f.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                ) : (
                  <input
                    type={f.field_type === 'date' ? 'date' : f.field_type === 'number' ? 'number' : f.field_type === 'email' ? 'email' : 'text'}
                    required={!!f.required}
                    value={custom[f.field_key] || ''}
                    onChange={setCustomField(f.field_key)}
                  />
                )}
              </div>
            ))}
          </div>
          {groupId && tplEntry === undefined && <div className="hint" style={{ marginBottom: 8 }}>Loading this company's custom fields…</div>}
          <div className="note" style={{ marginBottom: 14 }}>
            If this email already has an account, their existing name/DOB/phone/address/gender are kept
            as-is — an addition never overwrites a person's profile, even if these fields differ, and
            gender isn't required in that case either (only when this row creates a brand-new person).
            Date of birth must still be entered as a valid date — it is always checked, even for an
            existing person.
            Creates one enrolment per product selected above — all or nothing: if they
            already hold an active enrolment for any one of them, none are created.
          </div>
          <div className="btn-row">
            <button type="submit" className="btn pri" disabled={busy || !products.length}>{busy ? 'Adding…' : 'Add'}</button>
          </div>
          {!products.length && <div className="hint" style={{ marginTop: 8 }}>This company has no active products attached — add or reactivate one under Products before inviting anyone.</div>}
        </form>
      </div>

      <div className="sec-h"><h3>Or upload many at once</h3></div>
      <BulkUploadPanel expectedAction="ADDITION" actionLabel="addition" />
    </>
  );
}

const PROFILE_FIELDS = [
  ['first_name', 'First name', 'text'],
  ['last_name', 'Last name', 'text'],
  ['dob', 'Date of birth', 'date'],
  ['phone', 'Phone', 'tel'],
  ['address', 'Address', 'text'],
  ['gender', 'Gender', 'select'],
];

function UpdatePanel() {
  // Same search-then-drawer shape as RemovalPanel below — pick a person
  // from a searchable roster and the correction form opens in a drawer
  // (UpdateDrawer), instead of a single <select> combobox of everyone.
  // Unlike Removal, anyone in the group can be corrected, so the roster
  // isn't filtered by has_active_enrolment.
  const { groupId, curGroup, employeesIn, employeesStatus, loadEmployees } = useIcibAdmin();
  const { openDrawer } = useDrawer();
  useEffect(() => { loadEmployees(groupId); }, [groupId, loadEmployees]);
  const allEmployees = groupId ? employeesIn(groupId) : [];
  const [q, setQ] = useState('');
  const employees = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return allEmployees;
    return allEmployees.filter((emp) => `${emp.user?.first_name || ''} ${emp.user?.last_name || ''} ${emp.user?.email || ''}`.toLowerCase().includes(needle));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, employeesIn, q]);

  function open(emp) {
    openDrawer(
      `${emp.user?.first_name || ''} ${emp.user?.last_name || ''}`.trim() || emp.user?.email,
      'ICIB-02b · Update personal details',
      <UpdateDrawer emp={emp} />
    );
  }

  if (!groupId) return <ScopeNeeded />;

  return (
    <>
      <div className="card card-b">
        <div className="card-h" style={{ padding: 0, border: 0, marginBottom: 10 }}>
          <h4>Correct personal details — {curGroup()?.name}</h4>
          <span className="spacer" />
          <span className="ep">PATCH /companies/{'{company_id}'}/employees/{'{id}'}</span>
        </div>

        <div className="field">
          <label>Person <span className="req">*</span></label>
          <div className="filterbar" style={{ marginBottom: 0 }}>
            <input
              type="search"
              placeholder="Search name or email…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              disabled={employeesStatus !== 'ready'}
            />
          </div>
          {employeesStatus !== 'ready' ? (
            <div className="hint">Loading the register…</div>
          ) : !allEmployees.length ? (
            <div className="hint">No one is registered in this company yet.</div>
          ) : (
            <div className="tbl-wrap" style={{ marginTop: 8, maxHeight: 260, overflowY: 'auto' }}>
              <table>
                <tbody>
                  {employees.map((emp) => (
                    <tr key={emp.id} className="clickable" onClick={() => open(emp)}>
                      <td>
                        <span className="strong">{emp.user?.first_name} {emp.user?.last_name}</span><br />
                        <span className="mono dim">{emp.user?.email}</span>
                      </td>
                    </tr>
                  ))}
                  {!employees.length && (
                    <tr><td className="dim">No matches for &quot;{q}&quot;.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="sec-h"><h3>Or upload many at once</h3></div>
      <BulkUploadPanel expectedAction="UPDATE" actionLabel="update" />
    </>
  );
}

// Drawer content for UpdatePanel — the personal-details correction form
// for one person, prefilled from their roster entry. Owns its own submit,
// same as RemovalDrawer, and closes itself once the PATCH lands.
function UpdateDrawer({ emp }) {
  const { savePerson } = useIcibAdmin();
  const { closeDrawer } = useDrawer();
  const toast = useToast();
  const [form, setForm] = useState(() => ({
    first_name: emp.user?.first_name || '',
    last_name: emp.user?.last_name || '',
    dob: emp.user?.dob || '',
    phone: emp.user?.phone || '',
    address: emp.user?.address || '',
    gender: emp.user?.gender || '',
    effective_date: today(),
  }));
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    const res = await savePerson(emp.id, form);
    setBusy(false);
    if (res?.error) {
      toast(res.message || 'Could not update this person.', 'bad', res.error);
      return;
    }
    // §2.6a — a future effective_date defers this to a PendingIntakeRow
    // (202) instead of writing the correction now.
    if (res.status === 'scheduled') {
      toast(`Scheduled for ${res.effective_date} — will apply automatically that day (see Pending enrolment).`, 'ok');
    } else {
      toast(`Updated ${res.updated_fields ? res.updated_fields.join(', ') : 'personal details'}.`, 'ok');
    }
    closeDrawer();
  }

  return (
    <>
      <div className="sec-h" style={{ marginTop: 0 }}><h3>Personal details</h3><span className="spacer" /><span className="ep">PATCH .../employees/{'{id}'}</span></div>
      <DefinitionList rows={[['Email', <span className="mono">{emp.user?.email || '—'}</span>]]} />
      <form onSubmit={submit} style={{ marginTop: 12 }}>
        <div className="grid g2">
          {PROFILE_FIELDS.map(([key, label, type]) => (
            <div className="field" key={key} style={key === 'address' ? { gridColumn: '1 / -1' } : undefined}>
              <label>{label}</label>
              {type === 'select' ? (
                <select value={form[key] || ''} onChange={set(key)}>
                  <option value="">—</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              ) : (
                <input type={type} max={key === 'dob' ? todayISO() : undefined} value={form[key] || ''} onChange={key === 'phone' ? (e) => setForm((f) => ({ ...f, phone: phoneChars(e.target.value) })) : set(key)} />
              )}
            </div>
          ))}
          <div className="field">
            <label>Effective date <span className="req">*</span></label>
            <input type="date" required value={form.effective_date || ''} onChange={set('effective_date')} />
            <div className="hint">Today or earlier saves this correction now. A future date schedules it instead — see Pending enrolment.</div>
          </div>
        </div>
        <div className="note" style={{ marginBottom: 14 }}>
          Personal details only — no product, no cost centre, no reason (Update has no enrolment
          to attach one to). To correct a company-specific
          <span className="ep" style={{ marginLeft: 6 }}>form_data</span> value, use the admin override
          (ICIB-02d) instead; to change products, use Removal then a fresh Addition.
        </div>
        <div className="btn-row">
          <button type="submit" className="btn pri" disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</button>
        </div>
      </form>
    </>
  );
}

function RemovalPanel() {
  // ICIB-174 — a Removal now identifies its target by email +
  // subsidised_product_level together, ending only the enrolment(s) for
  // the product(s) selected, not every active enrolment the employee
  // holds. The single-item DELETE endpoint (still scoped to one
  // enrolment_id, §ICIB-02c/EMP-04) doesn't match this shape either, so
  // this still submits a one-row bulk-upload rather than calling it.
  const { groupId, curGroup, employeesIn, employeesStatus, loadEmployees } = useIcibAdmin();
  const { openDrawer } = useDrawer();
  useEffect(() => { loadEmployees(groupId); }, [groupId, loadEmployees]);
  // ICIB-01a's GET .../employees/summary now returns has_active_enrolment
  // per employee directly (BrandonUserStoryAPI.md ICIB-01a) — filter the
  // picker to whoever still has something to end, same as EMP-04's
  // equivalent panel, off the one roster fetch above with no extra call.
  const rosterCount = groupId ? employeesIn(groupId).length : 0;
  const activeEmployees = groupId ? employeesIn(groupId).filter((e) => e.has_active_enrolment) : [];
  const [q, setQ] = useState('');
  // Client-side name/email search over the active-enrolment roster — same
  // filter shape as EmployeesView's register search, just scoped to the
  // narrower "still has something to end" list above.
  const employees = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return activeEmployees;
    return activeEmployees.filter((emp) => `${emp.user?.first_name || ''} ${emp.user?.last_name || ''} ${emp.user?.email || ''}`.toLowerCase().includes(needle));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, employeesIn, q]);

  // Same picker-opens-a-drawer pattern as EmployeesView's own register —
  // the drawer content (RemovalDrawer) does its own ICIB-01b detail fetch
  // and owns the removal form/submit itself, same as EmployeeDrawer owns
  // its read-only view of the same data.
  function open(emp) {
    openDrawer(
      `${emp.user?.first_name || ''} ${emp.user?.last_name || ''}`.trim() || emp.user?.email,
      'ICIB-02c · Remove enrolment',
      <RemovalDrawer gid={groupId} emp={emp} />
    );
  }

  if (!groupId) return <ScopeNeeded />;

  return (
    <>
      <div className="card card-b">
        <div className="card-h" style={{ padding: 0, border: 0, marginBottom: 10 }}>
          <h4>Remove this person — {curGroup()?.name}</h4>
          <span className="spacer" />
          <span className="ep">POST /companies/{'{company_id}'}/employees/bulk-upload</span>
        </div>

        <div className="field">
          <label>Person <span className="req">*</span></label>
          <div className="filterbar" style={{ marginBottom: 0 }}>
            <input
              type="search"
              placeholder="Search name or email…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              disabled={employeesStatus !== 'ready'}
            />
          </div>
          {employeesStatus !== 'ready' ? (
            <div className="hint">Loading who still has an active enrolment…</div>
          ) : !activeEmployees.length ? (
            <div className="hint">{rosterCount ? 'Everyone in this company has already been removed.' : 'No one is registered in this company yet.'}</div>
          ) : (
            <div className="tbl-wrap" style={{ marginTop: 8, maxHeight: 260, overflowY: 'auto' }}>
              <table>
                <tbody>
                  {employees.map((emp) => (
                    <tr key={emp.id} className="clickable" onClick={() => open(emp)}>
                      <td>
                        <span className="strong">{emp.user?.first_name} {emp.user?.last_name}</span><br />
                        <span className="mono dim">{emp.user?.email}</span>
                      </td>
                    </tr>
                  ))}
                  {!employees.length && (
                    <tr><td className="dim">No matches for &quot;{q}&quot;.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="sec-h"><h3>Or upload many at once</h3></div>
      <BulkUploadPanel expectedAction="REMOVAL" actionLabel="removal" />
    </>
  );
}

// Drawer content for RemovalPanel — same read-only shape as EmployeesView's
// EmployeeDrawer (Contact + one card per enrolment with a LifeRail status
// badge), just with an active enrolment's card header carrying a checkbox
// instead of being purely informational, and the removal effective
// date/reason/submit form appended once at least one enrolment is
// selectable. A removed/archived enrolment's card stays exactly as
// EmployeeDrawer renders it — no checkbox, nothing to act on there.
function RemovalDrawer({ gid, emp }) {
  const { employeeDetails, loadEmployeeDetail, bulkUploadEmployees, product } = useIcibAdmin();
  const { closeDrawer } = useDrawer();
  const toast = useToast();
  // loadEmployeeDetail isn't stable across renders — key off emp.id alone,
  // same pattern as EmployeesView's EmployeeDrawer.
  useEffect(() => { loadEmployeeDetail(gid, emp.id); }, [gid, emp.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const detail = employeeDetails[emp.id];
  const enrolments = detail?.enrolments || [];
  const activeEnrolments = enrolments.filter((en) => en.status !== 'removed' && en.status !== 'archived');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  function toggleProduct(productId) {
    setSelectedProducts((cur) => (cur.includes(productId) ? cur.filter((p) => p !== productId) : [...cur, productId]));
  }

  async function submit(e) {
    e.preventDefault();
    const email = emp.user?.email;
    if (!email || !effectiveDate || !reason || !selectedProducts.length) return;
    setBusy(true);
    const res = await bulkUploadEmployees('single-removal.csv', [
      { action: 'REMOVAL', email, subsidised_product_level: selectedProducts, effective_date: effectiveDate, reason },
    ]);
    setBusy(false);
    if (res?.error) {
      toast(res.message || 'Could not remove this person.', 'bad', res.error);
      return;
    }
    const row = res?.results?.[0];
    if (!res?.committed || row?.status === 'error') {
      toast(row?.message || 'Could not remove this person.', 'bad', row?.status);
      return;
    }
    // §2.6a — a future effective_date defers this to a PendingIntakeRow
    // (still counts as "scheduled" here) instead of ending anything now.
    // A product whose Addition was still waiting on eligibility_days is
    // cancelled rather than ended (cancelled_pending_additions) — no
    // enrolment existed, so no removal confirmation is raised for it.
    const ended = row.enrolments?.length || 0;
    const cancelled = row.cancelled_pending_additions?.length || 0;
    const cancelledText = `${cancelled} pending addition${cancelled === 1 ? '' : 's'}`;
    if (row.status === 'scheduled') {
      toast(`Scheduled for ${row.effective_date} — will apply automatically that day (see Pending enrolment).`, 'ok');
    } else if (!ended) {
      toast(`Cancelled ${cancelledText} — they left before becoming eligible, so nothing was enrolled.`, 'ok');
    } else {
      toast(`Removed — ended ${ended} enrolment${ended === 1 ? '' : 's'}${cancelled ? `, cancelled ${cancelledText}` : ''}. Awaiting confirmation.`, 'ok');
    }
    closeDrawer();
  }

  return (
    <>
      <div className="sec-h" style={{ marginTop: 0 }}><h3>Enrolments</h3><span className="spacer" /><span className="ep">POST .../bulk-upload</span></div>
      {!detail ? (
        <div className="empty"><b>Loading…</b></div>
      ) : enrolments.length ? (
        <form onSubmit={submit}>
          {enrolments.map((en) => {
            const isActive = en.status !== 'removed' && en.status !== 'archived';
            return (
              <div className="card card-b" key={en.id} style={{ marginBottom: 10 }}>
                <div className="card-h" style={{ padding: 0, border: 0, marginBottom: 8 }}>
                  {isActive ? (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input type="checkbox" checked={selectedProducts.includes(en.product_id)} onChange={() => toggleProduct(en.product_id)} />
                      <b>{product(en.product_id)?.name || en.product_id}</b>
                    </label>
                  ) : (
                    <b>{product(en.product_id)?.name || en.product_id}</b>
                  )}
                  <span className="spacer" />
                  <LifeRail status={en.status} withLabel />
                </div>
                <DefinitionList rows={[
                  ['Invited', fmtDT(en.invited_at)],
                  ['Employment effective date', fmtDate(en.effective_date)],
                  ['Reason', en.reason || '—'],
                  ['Removal reason', en.removal_reason || '—'],
                  ['Removal effective date', fmtDate(en.removal_effective_date)],
                ]} />
              </div>
            );
          })}

          {activeEnrolments.length ? (
            <>
              <div className="field">
                <label>Removal Effective Date <span className="req">*</span></label>
                <input type="date" required value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
                <div className="hint">Today or earlier ends the selected enrolment(s) now. A future date (e.g. their last day) schedules it instead — see Pending enrolment.</div>
              </div>
              <div className="field">
                <label>Reason <span className="req">*</span></label>
                <input required value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Resigned" />
              </div>
              <div className="note warn">
                Ends only the enrolment(s) checked above — pick more than one to end several
                at once. Each one is set to <span className="ep">removed</span> and immediately flagged
                for your confirmation once the insurer has actioned it.
              </div>
              <div className="btn-row" style={{ marginTop: 14 }}>
                <button type="submit" className="btn danger" disabled={busy || !effectiveDate || !reason || !selectedProducts.length}>{busy ? 'Removing…' : 'Remove person'}</button>
              </div>
            </>
          ) : (
            <div className="hint">No active enrolments left to remove.</div>
          )}
        </form>
      ) : <div className="empty"><b>No enrolments</b></div>}
    </>
  );
}

// ICIB-02 bulk — shared by all three tabs, since they're really one
// endpoint (POST /companies/{company_id}/employees/bulk-upload) distinguished
// by each row's own `action` column, not by which tab uploaded it. A file
// can legitimately mix actions (README §2.6) — `expectedAction` only
// drives a soft warning per row, never a block, so a genuinely mixed file
// still uploads fine from whichever tab is more convenient.
function BulkUploadPanel({ expectedAction, actionLabel }) {
  const { groupId, curGroup, gProducts, activeTemplates, loadActiveTemplate, bulkUploadEmployees } = useIcibAdmin();
  const toast = useToast();
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState([]);
  // A filled-in template can run to hundreds of rows — page the preview
  // rather than rendering the whole file at once. Upload still sends
  // every row, not just the visible page.
  const rowsPage = usePagination(rows);
  const [busy, setBusy] = useState(false);
  const [buildingXlsx, setBuildingXlsx] = useState(false);
  const [result, setResult] = useState(null);
  // The per-row log is as long as the file — paged like the preview.
  const resultPage = usePagination(result?.results || []);

  const headers = rows.length ? Object.keys(rows[0]) : [];
  const mismatched = rows.filter((r) => r.action && r.action.toUpperCase() !== expectedAction).length;
  // ICIB-174 — subsidised_product_level resolution (names -> ids) needs
  // the group's currently-active products, same set AdditionPanel offers.
  const products = groupId ? gProducts(groupId).filter((gp) => gp.is_active !== false) : [];

  // Addition also writes group-specific form_data fields (README §2.6) —
  // pull the group's active template's field_keys the same way
  // AdditionPanel does (ICIB-10a's `?active=true`, not ICIB-11l — that
  // endpoint's `form_fields` was removed, since no form_data field is ever
  // a valid merge token any more) so the focused Addition template
  // includes them; Update/Removal never touch form_data, so they don't
  // need this.
  useEffect(() => {
    if (expectedAction === 'ADDITION' && groupId) loadActiveTemplate(groupId);
  }, [expectedAction, groupId, loadActiveTemplate]);
  const tplFields = expectedAction === 'ADDITION' && groupId ? activeTemplates[groupId]?.fields : undefined;
  const customFields = tplFields ? tplFields.filter((f) => !NOT_A_FORM_DATA_FIELD(f)) : [];
  const customFieldKeys = customFields.map((f) => f.field_key);
  // EXTR-13 — the template's column headings follow ICIB's own sheet;
  // fromHeadings turns them back into field_keys when a file is read.
  const headings = { ...columnHeadings(customFields, products), ...ACTION_HEADINGS[expectedAction] };
  // A second pass with the shared headings still reads a file downloaded
  // before an ACTION_HEADINGS override existed (e.g. an older Update
  // template headed "Employment or Termination Effective Date").
  const toFieldKeys = (parsed) => fromHeadings(fromHeadings(parsed, headings), COLUMN_HEADINGS);

  // ICIB-174 — ADDITION and REMOVAL rows carry subsidised_product_level,
  // a JSON array of one or more product ids on the real upload; the .xlsx
  // gives each product its own column with a Yes/No in-cell dropdown,
  // folded back into ids at upload time by toUploadRow. The focused
  // template also gets two other real Excel
  // dropdowns: `gender` (fixed to male/female) on any action that carries
  // it, and any custom field_type 'dropdown' field from the group's
  // active template (e.g. nz_citizen_or_visa, §ICIB-10b, fixed to
  // Yes/No); every date column (the fixed dob/effective_date plus any
  // custom field_type 'date' field) gets formatted YYYY-MM-DD with a
  // header note, since Excel otherwise free-formats a date column.
  async function onDownloadFocused() {
    const cols = withProductColumns(withFormDataColumns(ACTION_COLUMNS[expectedAction], customFieldKeys), products);
    // Removal's product columns default to No on the starter row, so only
    // the product(s) explicitly switched to Yes get ended.
    const productNames = new Set(expectedAction === 'REMOVAL' ? products.map((p) => p.name) : []);
    const starterRow = cols.map((c) => (c === 'action' ? expectedAction : c === 'effective_date' ? today() : productNames.has(c) ? 'No' : ''));
    const base = `${curGroup()?.company_code || 'company'}_${expectedAction.toLowerCase()}_template`;
    const listDropdowns = [
      ...(cols.includes('gender') ? [{ header: 'gender', values: ['male', 'female'] }] : []),
      ...(ACTION_COLUMNS[expectedAction].includes('subsidised_product_level') ? productDropdowns(products) : []),
      ...customFields.filter((f) => f.field_type === 'dropdown' && f.options?.length).map((f) => ({ header: f.field_key, values: f.options })),
    ];
    const dateColumns = [...DATE_COLUMNS[expectedAction], ...customFields.filter((f) => f.field_type === 'date').map((f) => f.field_key)];
    setBuildingXlsx(true);
    try {
      const { downloadXlsxTemplate } = await loadXlsxUtils();
      await downloadXlsxTemplate({
        filename: `${base}.xlsx`,
        starterRow,
        icibStyle: true,
        ...withHeadings({ headers: cols, listDropdowns, dateColumns }, headings),
      });
    } finally {
      setBuildingXlsx(false);
    }
  }

  function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setResult(null);
    setFileName(file.name);
    rowsPage.setPage(1);
    if (/\.xlsx$/i.test(file.name)) {
      loadXlsxUtils().then(({ parseXlsx }) => file.arrayBuffer().then(parseXlsx)).then((parsed) => setRows(toFieldKeys(parsed)));
    } else {
      file.text().then((text) => setRows(toFieldKeys(parseCsv(text))));
    }
  }


  async function onUpload() {
    if (!rows.length) return;
    // ICIB-174 — fold each row's Yes/No product columns into the id array
    // the bulk endpoint expects; company_code isn't sent (utils/productColumns.js).
    const { rows: uploadRows, problems } = prepareUploadRows(rows, products);
    if (problems.length) { toast(problems.join(' '), 'bad'); return; }
    if (!uploadRows.length) { toast('No filled-in rows to upload.', 'bad'); return; }
    setBusy(true);
    const res = await bulkUploadEmployees(fileName, uploadRows);
    setBusy(false);
    if (res?.error) { toast(res.message || 'Could not process this file.', 'bad', res.error); return; }
    setResult({ ...res, sentRows: uploadRows });
    resultPage.setPage(1);
    if (res.committed) {
      toast(`${res.rows_processed} row(s) committed.`, 'ok');
      setFileName(''); setRows([]);
    } else {
      toast('Some rows failed — nothing was written. Fix and re-upload.', 'bad');
    }
  }

  return (
    <div className="card card-b">
      <div className="card-h" style={{ padding: 0, border: 0, marginBottom: 10 }}>
        <h4>Bulk {actionLabel}</h4>
        <span className="spacer" />
        <span className="ep">POST /companies/{'{company_id}'}/employees/bulk-upload</span>
      </div>

      <div className="btn-row" style={{ marginBottom: 6 }}>
        <button type="button" className="btn sm" onClick={onDownloadFocused} disabled={buildingXlsx}>
          {buildingXlsx ? 'Building…' : `Download ${actionLabel}-only template (.xlsx)`}
        </button>
      </div>
      <div className="hint" style={{ marginBottom: 12 }}>
        The {actionLabel}-only template has just the columns {actionLabel} rows need — action
        pre-filled, no columns you'd have to leave blank or that would get this row rejected
        (e.g. Update rejects any non-profile field).
        {(expectedAction === 'ADDITION' || expectedAction === 'REMOVAL') && (
          ' Each product has its own column with a Yes/No dropdown — mark Yes for every product that applies to that person.'
        )}
        {' '}Date columns ({DATE_COLUMNS[expectedAction].map((c) => headings[c]).join(', ')}) are
        formatted YYYY-MM-DD with a note on the header cell{expectedAction === 'ADDITION' && ', Biological Sex is a real in-cell dropdown limited to male/female, and NZ Citizen or 2 year work Visa is a real in-cell dropdown limited to Yes/No'}.
      </div>
      <div className="field">
        <label>Completed file</label>
        <input type="file" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={onFile} />
        <div className="hint">
          The .xlsx template above, or a plain CSV with the same headers — either uploads the same
          way. A file can mix actions freely (README §2.6) — this tab is just a convenient place to
          upload one that's mostly {actionLabel.toLowerCase()} rows.
        </div>
      </div>

      {rows.length > 0 && (
        <>
          {mismatched > 0 && (
            <div className="note warn" style={{ marginBottom: 10 }}>
              {mismatched} row(s) have an action other than {expectedAction} — that's fine, each row is
              processed by its own action column regardless of which tab uploaded the file.
            </div>
          )}
          <div className="tbl-wrap" style={{ marginBottom: 10 }}>
            <table>
              <thead><tr>{headers.map((h) => <th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {rowsPage.pageItems.map((r, i) => (
                  <tr key={i}>{headers.map((h) => <td key={h} className="mono">{r[h]}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginBottom: 10 }}><Pagination {...rowsPage} /></div>
          <div className="btn-row">
            <button type="button" className="btn pri sm" disabled={busy} onClick={onUpload}>{busy ? 'Uploading…' : `Upload ${rows.length} row(s) as one batch`}</button>
          </div>
          <div className="hint" style={{ marginTop: 6 }}>
            Atomic (README §2.6) — every row must pass or nothing is written.
          </div>
        </>
      )}

      {result && (
        <div className={'note' + (result.committed ? ' ok' : ' bad')} style={{ marginTop: 12 }}>
          <b>{result.committed ? 'Committed' : 'Rejected — nothing written'}</b>
          {resultPage.pageItems.map((r) => (
            <div key={r.row} className="mono" style={{ fontSize: 11.5 }}>
              row {r.row} ({r.action}){resultRowEmail(result, r) && ` ${resultRowEmail(result, r)}`}: {r.status}{r.message ? ` — ${r.message}` : ''}
              {rowDetail(r)}
            </div>
          ))}
          <Pagination {...resultPage} />
        </div>
      )}

      {!groupId && <div className="hint" style={{ marginTop: 8 }}>Select a company above before uploading.</div>}
    </div>
  );
}

function ScopeNeeded() {
  return (
    <div className="card">
      <div className="empty">
        <b>Select a company</b>
        Choose a company from the scope selector above to process its intake.
      </div>
    </div>
  );
}

export default IntakeView;
